import React, { useState, useEffect } from 'react';
import ChangePasswordModal from './common/ChangePasswordModal';
import useAppVersion from '../hooks/useAppVersion';

/**
 * 상단 바 — VIMS 와 같은 형태 (2026-09-21)
 *
 * 예전에는 로고·버전·시계·날씨·사용자·비밀번호 변경이 모두 사이드바 안에 있어서
 * 메뉴가 아래로 밀리고 화면이 좁아 보였다. VIMS 가 이 정보를 상단 바로 올린 것과
 * 같은 모양으로 맞춘다. 사이드바에는 메뉴만 남는다.
 */
const TopBar = ({ user, onLogout, onToggleSidebar, compact }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showPwModal, setShowPwModal] = useState(false);
  // 버전은 모바일 화면과 같은 곳(개발 메모)에서 같은 방법으로 읽는다
  const { label: versionLabel } = useAppVersion();
  const [weather, setWeather] = useState({ today: '…', tomorrow: '…' });

  // 실시간 시계 (1초마다)
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 날씨 (Open-Meteo, 올림픽공원 좌표) — 30분마다 갱신
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const lat = 37.5208;
        const lon = 127.1214;
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&daily=temperature_2m_max,weather_code&timezone=Asia/Seoul`
        );
        const data = await response.json();

        const getWeatherStatus = (code, temp) => {
          const t = Math.round(temp);
          if (code === 0) return `☀️ ${t}°`;
          if ([1, 2, 3].includes(code)) return `⛅ ${t}°`;
          if ([45, 48].includes(code)) return `🌫️ ${t}°`;
          if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(code)) return `🌧️ ${t}°`;
          if ([71, 73, 75, 77, 85, 86].includes(code)) return `❄️ ${t}°`;
          if ([95, 96, 99].includes(code)) return `⚡ ${t}°`;
          return `☀️ ${t}°`;
        };

        if (data && data.current && data.daily) {
          setWeather({
            today: getWeatherStatus(data.current.weather_code, data.current.temperature_2m),
            tomorrow: getWeatherStatus(data.daily.weather_code[1], data.daily.temperature_2m_max[0])
          });
        }
      } catch (error) {
        console.error('날씨 API 동기화 실패:', error);
        setWeather({ today: '☀️ 22°', tomorrow: '☁️ 19°' });
      }
    };
    fetchWeather();
    const weatherTimer = setInterval(fetchWeather, 1800000);
    return () => clearInterval(weatherTimer);
  }, []);

  const yy = currentTime.getFullYear();
  const mm = String(currentTime.getMonth() + 1).padStart(2, '0');
  const dd = String(currentTime.getDate()).padStart(2, '0');
  const dateString = `${yy}-${mm}-${dd}`;
  const timeString = currentTime.toTimeString().split(' ')[0];

  const S = {
    bar: {
      position: 'fixed', top: 0, left: 0, right: 0, height: '64px', zIndex: 3000,
      background: '#1e293b', color: '#fff', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', padding: '0 20px', boxSizing: 'border-box',
      borderBottom: '1px solid rgba(255,255,255,0.08)', fontFamily: 'sans-serif'
    },
    left: { display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 },
    right: { display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#cbd5e1', fontWeight: 600 },
    toggle: {
      background: 'rgba(255,255,255,0.1)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: '8px', width: '38px', height: '38px', cursor: 'pointer', fontSize: '16px', flex: 'none'
    },
    logoIcon: { color: '#ff9f43', fontSize: '20px' },
    logo: { fontWeight: 900, fontSize: '22px', color: '#11caa0', letterSpacing: '-0.5px' },
    ver: {
      fontSize: '13px', fontWeight: 900, color: '#fde047',
      background: 'rgba(255,255,255,0.1)', padding: '2px 7px', borderRadius: '5px', whiteSpace: 'nowrap'
    },
    sub: { fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' },
    weather: { display: 'flex', gap: '10px', fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' },
    clock: { display: 'flex', alignItems: 'baseline', gap: '8px', lineHeight: 1 },
    clockDate: { fontSize: '11px', color: '#94a3b8' },
    // 시각은 VIMS 와 같은 전광판(7-세그먼트) 글꼴 — App.css 의 @font-face 'Segment7'. 색은 CRM 민트 그대로(2026-10-30)
    clockTime: { fontSize: '18px', color: '#11caa0', fontFamily: "'Segment7', monospace", textShadow: '0 0 8px rgba(17,202,160,0.35)' },
    userBox: { display: 'flex', alignItems: 'center', gap: '6px' },
    userName: { color: '#11caa0', fontWeight: 800 },
    out: {
      padding: '2px 8px', fontSize: '10px', cursor: 'pointer', color: '#e2e8f0',
      border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', borderRadius: '5px'
    },
    pwBtn: {
      padding: '7px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
      background: 'rgba(255,255,255,0.08)', color: '#e2e8f0',
      border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px'
    }
  };

  return (
    <>
      <header style={S.bar}>
        <div style={S.left}>
          <button style={S.toggle} onClick={onToggleSidebar} title="메뉴 접기/펼치기" aria-label="메뉴 접기/펼치기">☰</button>
          <span style={S.logoIcon}>⚡</span>
          <span style={S.logo}>E2B-CRM</span>
          <span style={S.ver}>{versionLabel}</span>
          {!compact && <span style={S.sub}>고객·차량·정비 통합관리</span>}
          {/* 사내 시스템 전환 메뉴가 붙는 자리 — public/switcher.js 가 채운다 */}
          <div id="sys-switch-slot"></div>
        </div>

        <div style={S.right}>
          {!compact && (
            <div style={S.weather}>
              <span>오늘 {weather.today}</span>
              <span>내일 {weather.tomorrow}</span>
            </div>
          )}
          {!compact && (
            <div style={S.clock}>
              <span style={S.clockDate}>{dateString}</span>
              <span style={S.clockTime}>{timeString}</span>
            </div>
          )}
          <div style={S.userBox}>
            <span style={S.userName}>{user?.name}</span>
            <button
              style={S.out}
              onClick={() => { if (window.confirm('로그아웃 하시겠습니까?')) onLogout(); }}
            >OUT</button>
          </div>
          <button style={S.pwBtn} onClick={() => setShowPwModal(true)}>🔑 비밀번호 변경</button>
        </div>
      </header>

      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
    </>
  );
};

export default TopBar;
